import { env } from '@zopauk/nextjs-env';

/**
 * Resolve the DatoCMS environment (the `x-environment` header value) to target
 * for the current app environment.
 *
 * In production the environment is named per release — `prod-<version>` with
 * dots turned into dashes (e.g. `0.0.1234` → `prod-0-0-1234`). scripts/migrations.js
 * forks and promotes that environment to primary on every prod deploy, so the
 * header MUST match its name exactly; this derivation mirrors that script.
 *
 * The version is read from the build-time inlined `process.env.NEXT_PUBLIC_APP_VERSION`
 * (baked from `K8S_APP_VERSION` by next.config into both the server and client
 * bundles). It deliberately does NOT use `env('NEXT_PUBLIC_APP_VERSION')`: that
 * helper does a runtime lookup, but the variable only exists as a build inline —
 * it is not present in the container env nor in `window.__ENV` — so at runtime it
 * resolves to `undefined` and yields the broken `prod-` value that 404s in DatoCMS.
 *
 * UAT always targets the `uat` environment, so it is hard-coded. Neither
 * production nor UAT therefore need `NEXT_PUBLIC_DATO_CMS_ENVIRONMENT` set in argo.
 *
 * Everywhere else (local dev, PR forks) the environment name comes from
 * `NEXT_PUBLIC_DATO_CMS_ENVIRONMENT`, supplied via `.env` files rather than argo.
 */
export const getDatoEnvironment = (): undefined | string => {
  const appEnv = env('NEXT_PUBLIC_APP_ENV');

  if (appEnv === 'production') {
    // eslint-disable-next-line no-process-env -- must read the build-time inline, not the runtime env() lookup; see JSDoc above
    const version = process.env.NEXT_PUBLIC_APP_VERSION;

    // Never emit the malformed `prod-` (a non-existent DatoCMS environment that
    // 404s). If the version is somehow unavailable — e.g. a build without
    // K8S_APP_VERSION — omit the header so DatoCMS serves the primary
    // environment instead of failing outright.
    if (!version) {
      return undefined;
    }

    return `prod-${version}`.replaceAll('.', '-').replace('-prod', '');
  }

  if (appEnv === 'uat') {
    return 'uat';
  }

  return env('NEXT_PUBLIC_DATO_CMS_ENVIRONMENT');
};
